import AtmosphericHero from '@/components/marketing/AtmosphericHero'
import ScrollReveal from '@/components/marketing/ScrollReveal'
import FaqAccordion from '@/components/marketing/FaqAccordion'
import type { FaqItem } from '@/components/marketing/FaqAccordion'
import type { SharedMarketingProps } from '@/types/marketing'

interface LinkItem {
  label: string
  href: string
}

interface CoverSongLicensingProps extends SharedMarketingProps {
  partner: {
    name: string
    logo_url: string
    seal_url: string
    home_url: string
    tagline: string
    note: string
  }
  hero: { eyebrow: string; title: string; body: string; actions: LinkItem[] }
  why: { eyebrow: string; title: string; lead: string; points: { title: string; body: string }[] }
  requirements: {
    eyebrow: string
    title: string
    lead: string
    points: { title: string; body: string }[]
    resources: { title: string; note: string; links: LinkItem[] }
  }
  how: { eyebrow: string; title: string; lead: string; steps: { step: string; title: string; body: string }[] }
  benefits: { eyebrow: string; title: string; items: string[] }
  seal: { title: string; body: string; points: string[] }
  more: { eyebrow: string; title: string; items: { title: string; body: string; link: LinkItem }[] }
  honest: { eyebrow: string; title: string; paragraphs: string[] }
  faq: FaqItem[]
  cta: { title: string; body: string; actions: LinkItem[] }
}

// Renders an internal Inertia-friendly anchor or an external partner link
// (target=_blank) based on the href. Partner links carry ?PartnerID=… so
// referrals are attributed — keep them as plain <a> with rel="noopener".
function ActionLink({ action, className }: { action: LinkItem; className: string }) {
  const external = action.href.startsWith('http')
  return external ? (
    <a href={action.href} className={className} target="_blank" rel="noopener">{action.label}</a>
  ) : (
    <a href={action.href} className={className}>{action.label}</a>
  )
}

export default function CoverSongLicensing(props: CoverSongLicensingProps) {
  const { partner, hero, why, requirements, how, benefits, seal, more, honest, faq, cta } = props

  return (
    <>
      {/* Hero — dark atmosphere. The shared AtmosphericHero paints an 8rem
          bottom fade (z-2) over its content (z-1), so we keep the CTAs well
          clear of it: a taller hero + a bottom spacer so the fade lands in
          empty space below the buttons, never on them. */}
      <AtmosphericHero minHeight="64vh" containerClassName="hero-content">
        <div className="hero-text">
          <span className="hero-eyebrow">{hero.eyebrow}</span>
          <h1 className="hero-headline" style={{ fontSize: 'clamp(2.5rem, 7vw, 6rem)' }}>{hero.title}</h1>
          <p className="hero-body">{hero.body}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            {hero.actions.map((action, index) => (
              <ActionLink
                key={action.href}
                action={action}
                className={index === 0 ? 'marketing-button-primary' : 'marketing-button-secondary'}
              />
            ))}
          </div>
          {/* Keeps the CTAs above the hero's bottom fade. */}
          <div aria-hidden="true" className="h-8 lg:h-12" />
        </div>
      </AtmosphericHero>

      {/* Provider — Easy Song wordmark on a LIGHT (cream) background */}
      <section className="marketing-section marketing-section-warm">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="mx-auto max-w-2xl text-center">
              <div className="marketing-eyebrow-mono" style={{ justifyContent: 'center' }}>Our licensing partner</div>
              <div className="mt-6 flex justify-center">
                <a
                  href={partner.home_url}
                  target="_blank"
                  rel="noopener"
                  aria-label={`${partner.name} (opens in a new tab)`}
                  className="inline-flex items-center justify-center rounded-2xl bg-white px-8 py-6 shadow-sm transition-shadow hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ff5400]"
                >
                  <img src={partner.logo_url} alt={partner.name} className="h-11 w-auto md:h-12" />
                </a>
              </div>
              <p className="mt-6 text-base leading-7 text-[#3b3b3b]">{partner.tagline}</p>
              <p className="mt-2 text-sm text-[#6b6b6b]">{partner.note}</p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Why you need a cover song licence */}
      <section className="marketing-section has-glow">
        <div className="section-glow cool" style={{ width: '40rem', height: '40rem', left: '-12rem', top: '-12rem' }} />
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-section-header">
              <div className="marketing-eyebrow-mono">{why.eyebrow}</div>
              <h2 className="marketing-section-title">{why.title}</h2>
              <p className="marketing-lead max-w-2xl">{why.lead}</p>
            </div>
          </ScrollReveal>
          <div className="grid gap-5 md:grid-cols-2">
            {why.points.map((point, index) => (
              <ScrollReveal key={point.title} direction="up" delay={index * 0.08}>
                <div className="marketing-surface-card h-full">
                  <h3 className="text-xl font-semibold text-white">{point.title}</h3>
                  <p className="mt-3 text-sm leading-7 text-white/72">{point.body}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Cover song requirements — dark, glow on the opposite side from "why" */}
      <section className="marketing-section has-glow">
        <div className="section-glow warm" style={{ width: '38rem', height: '38rem', right: '-12rem', bottom: '-12rem' }} />
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-section-header">
              <div className="marketing-eyebrow-mono">{requirements.eyebrow}</div>
              <h2 className="marketing-section-title">{requirements.title}</h2>
              <p className="marketing-lead max-w-2xl">{requirements.lead}</p>
            </div>
          </ScrollReveal>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {requirements.points.map((point, index) => (
              <ScrollReveal key={point.title} direction="up" delay={index * 0.06}>
                <div className="marketing-surface-card h-full">
                  <h3 className="text-lg font-semibold text-white">{point.title}</h3>
                  <p className="mt-3 text-sm leading-7 text-white/72">{point.body}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
          <ScrollReveal direction="up" delay={0.1}>
            <div className="marketing-surface-card mx-auto mt-8 max-w-3xl">
              <h3 className="text-base font-semibold text-white">{requirements.resources.title}</h3>
              <p className="mt-2 text-sm leading-7 text-white/72">{requirements.resources.note}</p>
              <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
                {requirements.resources.links.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="inline-flex items-center gap-1 text-sm font-semibold text-[#ff8a4c] hover:text-[#ff6a1a]"
                    target="_blank"
                    rel="noopener"
                  >
                    {link.label} →
                  </a>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* How it works — 3 steps on cream */}
      <section className="marketing-section marketing-section-warm">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-section-header">
              <div className="marketing-eyebrow-mono">{how.eyebrow}</div>
              <h2 className="marketing-section-title marketing-section-title-dark">{how.title}</h2>
              <p className="max-w-2xl text-base leading-7 text-[#3b3b3b]">{how.lead}</p>
            </div>
          </ScrollReveal>
          <div className="grid gap-5 md:grid-cols-3">
            {how.steps.map((step, index) => (
              <ScrollReveal key={step.step} direction="up" delay={index * 0.08}>
                <div className="marketing-light-card h-full">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#ff6a1a]/[0.12] text-base font-bold text-[#ff5400]">
                    {step.step}
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-[#151515]">{step.title}</h3>
                  <p className="mt-2 text-sm leading-7 text-[#3b3b3b]">{step.body}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* What you get + Seal of Authenticity (seal sits on a light card) */}
      <section className="marketing-section">
        <div className="container mx-auto px-6">
          <div className="grid gap-8 lg:grid-cols-2 lg:items-start">
            <ScrollReveal direction="up">
              <div>
                <div className="marketing-eyebrow-mono">{benefits.eyebrow}</div>
                <h2 className="marketing-section-title">{benefits.title}</h2>
                <ul className="mt-6 space-y-3">
                  {benefits.items.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm leading-7 text-white/78">
                      <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#ff6a1a]" aria-hidden="true" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>
            <ScrollReveal direction="up" delay={0.1}>
              <div className="rounded-[1.75rem] border border-white/8 bg-white p-8 text-center">
                <img src={partner.seal_url} alt="Easy Song Seal of Authenticity" className="mx-auto h-auto w-full max-w-[18rem]" />
                <h3 className="mt-6 text-lg font-semibold text-[#151515]">{seal.title}</h3>
                <p className="mt-2 text-sm leading-7 text-[#3b3b3b]">{seal.body}</p>
                <ul className="mt-4 space-y-2 text-left">
                  {seal.points.map((point) => (
                    <li key={point} className="flex items-start gap-2 text-sm leading-6 text-[#3b3b3b]">
                      <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#ff5400]" aria-hidden="true" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Beyond covers — custom licensing, copyrighting, memberships (no pricing) */}
      <section className="marketing-section marketing-section-warm">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-section-header">
              <div className="marketing-eyebrow-mono">{more.eyebrow}</div>
              <h2 className="marketing-section-title marketing-section-title-dark">{more.title}</h2>
            </div>
          </ScrollReveal>
          <div className="grid gap-5 md:grid-cols-3">
            {more.items.map((item, index) => (
              <ScrollReveal key={item.title} direction="up" delay={index * 0.08}>
                <div className="marketing-light-card flex h-full flex-col">
                  <h3 className="text-lg font-semibold text-[#151515]">{item.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-7 text-[#3b3b3b]">{item.body}</p>
                  <a
                    href={item.link.href}
                    className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#ff5400] hover:text-[#ff2200]"
                    target="_blank"
                    rel="noopener"
                  >
                    {item.link.label} →
                  </a>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* How wavbee fits in — the honest "we point you, we don't broker" note */}
      <section className="marketing-section">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-surface-card mx-auto max-w-3xl">
              <div className="marketing-eyebrow-mono">{honest.eyebrow}</div>
              <h2 className="mt-3 text-xl font-semibold text-white">{honest.title}</h2>
              {honest.paragraphs.map((paragraph, index) => (
                <p key={index} className="mt-3 text-sm leading-7 text-white/72">{paragraph}</p>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* FAQ — cream */}
      <section className="marketing-section marketing-section-warm">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-section-header">
              <div className="marketing-eyebrow marketing-eyebrow-dark">FAQ</div>
              <h2 className="marketing-section-title marketing-section-title-dark">Cover licensing questions</h2>
            </div>
          </ScrollReveal>
          <ScrollReveal direction="up" delay={0.1}>
            <div className="mx-auto max-w-3xl">
              <FaqAccordion items={faq} />
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Final CTA — dark */}
      <section className="container mx-auto px-6 pb-20 pt-16">
        <ScrollReveal direction="up">
          <div className="marketing-final-cta mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-bold text-white">{cta.title}</h2>
            <p className="mt-3 text-white/64">{cta.body}</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              {cta.actions.map((action, index) => (
                <ActionLink
                  key={action.href}
                  action={action}
                  className={index === 0 ? 'marketing-button-primary' : 'marketing-button-secondary'}
                />
              ))}
            </div>
          </div>
        </ScrollReveal>
      </section>
    </>
  )
}
